import React, { useCallback, useRef, useState } from "react";
import { useDocumentCapturePhaseListener } from "Hooks/useDocumentCapturePhaseListener";
import injectIntl from "Util/injectIntl";
import {
  UncontrolledDropdown,
  DropdownItem,
  DropdownToggle,
  DropdownMenu,
  Input,
  NavbarBrand
} from "reactstrap";
import IntlMessages from "Util/IntlMessages";

import PerfectScrollbar from "react-perfect-scrollbar";

import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAppSelector } from "Redux/hooks";
import {
  setContainerClassnames,
  clickOnMobileMenu
} from "Redux/actions";

import { menuHiddenBreakpoint, searchPath } from "Constants/defaultValues";

import navbarLogo from "Assets/img/logo-reactify.png";
import notifications from "Data/topbar.notifications.json";
import getMessages from "Data/topbar.messages.json";

const PERFECT_SCROLL_OPTIONS = {
  suppressScrollX: true,
  wheelPropagation: false
};

const MOBILE_SEARCH_OUTSIDE_EVENTS = ["click"] as const;

function TopNav({ intl }: { intl: import("react-intl").IntlShape }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const containerClassnames = useAppSelector(s => s.menu.containerClassnames);
  const menuClickCount = useAppSelector(s => s.menu.menuClickCount);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);

  const closeMobileSearch = useCallback(() => {
    setMobileSearchOpen(false);
    setSearchKeyword("");
  }, []);

  const handleMobileSearchOutside = useCallback(
    (e: Event) => {
      const node = e.target instanceof Node ? e.target : null;
      if (node && searchWrapRef.current?.contains(node)) {
        return;
      }
      closeMobileSearch();
    },
    [closeMobileSearch]
  );

  useDocumentCapturePhaseListener(
    mobileSearchOpen,
    handleMobileSearchOutside,
    MOBILE_SEARCH_OUTSIDE_EVENTS
  );

  const handleSearchIconClick = useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      if (window.innerWidth < menuHiddenBreakpoint) {
        if (mobileSearchOpen) {
          navigate(searchPath + "/" + searchKeyword);
          setSearchKeyword("");
          setMobileSearchOpen(false);
        } else {
          setMobileSearchOpen(true);
        }
      } else {
        navigate(searchPath + "/" + searchKeyword);
        setSearchKeyword("");
      }
    },
    [mobileSearchOpen, navigate, searchKeyword]
  );

  const handleSearchInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchKeyword(e.target.value);
    },
    []
  );

  const handleSearchInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        navigate(searchPath + "/" + searchKeyword);
        setSearchKeyword("");
        setMobileSearchOpen(false);
      }
    },
    [navigate, searchKeyword]
  );

  const handleLogout = useCallback(() => {
    navigate("/login");
  }, [navigate]);

  const menuButtonClick = useCallback(
    (e: React.MouseEvent, count: number, classes: string) => {
      e.preventDefault();
      setTimeout(() => {
        const event = document.createEvent("HTMLEvents");
        event.initEvent("resize", false, false);
        window.dispatchEvent(event);
      }, 350);
      dispatch(setContainerClassnames(count + 1, classes));
    },
    [dispatch]
  );

  const mobileMenuButtonClick = useCallback(
    (e: React.MouseEvent, classes: string) => {
      e.preventDefault();
      dispatch(clickOnMobileMenu(classes));
    },
    [dispatch]
  );

  const searchClassName =
    "search" + (mobileSearchOpen ? " mobile-view" : "");

  return (
    <nav className="navbar fixed-top">
      <NavbarBrand
        tag={NavLink}
        to="/app/main/layout/main"
        className="navbar-brand navbar-logo navbar-logo--image ms-3"
      >
        <img src={navbarLogo} alt="Reactify" />
      </NavbarBrand>
      <NavLink
        to="#"
        className="menu-button d-none d-md-block"
        onClick={e =>
          menuButtonClick(e, menuClickCount, containerClassnames)
        }
      >
        <span className="fa fa-stream" />
      </NavLink>
      <NavLink
        to="#"
        className="menu-button-mobile d-block d-md-none"
        onClick={e => mobileMenuButtonClick(e, containerClassnames)}
      >
        <span className="fa fa-stream" />
      </NavLink>{" "}
      <div className="ms-auto">
        <div
          ref={searchWrapRef}
          className={searchClassName}
          data-search-path="/app/layouts/search"
        >
          <Input
            name="searchKeyword"
            id="searchKeyword"
            placeholder={intl.formatMessage({ id: "menu.search" })}
            value={searchKeyword}
            onChange={handleSearchInputChange}
            onKeyDown={handleSearchInputKeyDown}
          />
          <span
            className="search-icon"
            onClick={e => handleSearchIconClick(e)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSearchIconClick(e);
              }
            }}
          >
            <i className="simple-icon-magnifier" />
          </span>
        </div>
      </div>
      <div className="ms-auto">
        <div className="header-icons d-inline-block align-middle">
          <div className="position-relative d-inline-block">
            <UncontrolledDropdown className="dropdown-menu-right">
              <DropdownToggle
                className="header-icon notificationButton"
                color="empty"
              >
                <i className="far fa-envelope" />
                <span className="count">3</span>
              </DropdownToggle>
              <DropdownMenu
                className="position-absolute mt-3 scroll"
                right
                id="notificationDropdownMessage"
              >
                <p>
                  <IntlMessages id="topbar.messages" />
                  <span className="float-right">
                    {" "}
                    <span className="fa fa-check font-color-1" />{" "}
                    <a href="#">
                      {" "}
                      <IntlMessages id="topbar.mark-all" />
                    </a>
                  </span>
                </p>
                <div className="border-top mb-3" />
                {getMessages.data.map((message, index) => {
                  return (
                    <div
                      key={index}
                      className="d-flex flex-row mb-3 pb-3 border-bottom"
                    >
                      <a href="/app/layouts/details">
                        <img
                          src={message.image}
                          alt="Notification"
                          className="img-thumbnail list-thumbnail xsmall border-0 rounded-circle"
                        />
                      </a>
                      <span className={`${message.status}`}>.</span>
                      <div className="pl-3 pr-2">
                        <a href="/app/layouts/details">
                          <strong>{message.username}</strong>

                          <p className="font-weight-medium mb-1">
                            {message.message}
                          </p>
                          <p className="text-muted mb-0 text-small">
                            {message.time}
                          </p>
                        </a>
                      </div>
                    </div>
                  );
                })}

                <p className="text-center">
                  <a href="#">
                    <strong>
                      <IntlMessages id="topbar.view-all" />
                    </strong>
                  </a>
                </p>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>

          <div className="position-relative d-inline-block">
            <UncontrolledDropdown className="dropdown-menu-right">
              <DropdownToggle
                className="header-icon notificationButton"
                color="empty"
              >
                <i className="simple-icon-bell" />
                <span className="count">3</span>
              </DropdownToggle>
              <DropdownMenu
                className="position-absolute mt-3 scroll"
                right
                id="notificationDropdown"
              >
                <PerfectScrollbar option={PERFECT_SCROLL_OPTIONS}>
                  {notifications.data.map((notify, index) => {
                    return (
                      <div
                        key={index}
                        className="d-flex flex-row mb-3 pb-3 border-bottom"
                      >
                        <a href="/app/layouts/details">
                          <div className={`${notify.background}`}>
                            <span className={`${notify.icon}`} />
                          </div>
                        </a>
                        <div className="pl-3 pr-2">
                          <a href="/app/layouts/details">
                            <p className="font-weight-medium mb-1">
                              {notify.message}
                            </p>
                            <p className="text-muted mb-0 text-small">
                              {notify.date}
                            </p>
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </PerfectScrollbar>
              </DropdownMenu>
            </UncontrolledDropdown>
          </div>
        </div>
        <div className="user d-inline-block">
          <UncontrolledDropdown className="dropdown-menu-right">
            <DropdownToggle className="p-0" color="empty">
              <span className="name mr-1">Junaid Raza</span>
              <span>
                <img alt="Profile Picture" src="/assets/img/p4.png" />
              </span>
            </DropdownToggle>
            <DropdownMenu className="mt-3" right>
              <DropdownItem>
                <div className="float-left mr-3">
                  <img className="Profile Picture" src="/assets/img/p4.png" />{" "}
                </div>
                <div>
                  <strong>Junaid Raza</strong>
                  <p>Administrator</p>
                </div>
              </DropdownItem>
              <DropdownItem>
                <span className="fa fa-user font-color-1 mr-1" /> Profile
              </DropdownItem>
              <DropdownItem>
                <span className="fa fa-cog font-color-1 mr-1" /> Settings
              </DropdownItem>
              <DropdownItem>
                <span className="fa fa-edit font-color-1 mr-1" /> Tasks
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem onClick={handleLogout}>
                <span className="fa fa-power-off font-color-1 mr-1" /> Sign
                out
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </div>
    </nav>
  );
}

export default injectIntl(TopNav);
